import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import type { ChatStatus } from 'ai';
import { Box, Stack } from '@rossum/ui/material';
import { isStreamingStatus, offeredLookup } from '../hooks/chatStore';
import { ChatUIMessage } from '../hooks/UseAgentChat/schemas';
import { AssistantMessage, ChatMessage } from './ChatMessage';
import { LookupProposal } from './LookupProposal';

type ChatMessageListProps = {
  messages: ChatUIMessage[];
  status: ChatStatus;
  onFeedback?: (messageIndex: number, isPositive: boolean) => void;
};

export const ChatMessageList = ({
  messages,
  status,
  onFeedback,
}: ChatMessageListProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const isUserScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const isActive = isStreamingStatus(status);
  const lastMessage = messages.at(-1);
  const streamingMessage =
    isActive && lastMessage?.role === 'assistant' ? lastMessage : undefined;
  const readyMessages = streamingMessage ? messages.slice(0, -1) : messages;

  const handleScroll = useCallback(() => {
    isUserScrollingRef.current = true;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      isUserScrollingRef.current = false;

      const container = containerRef.current;
      if (!container) return;

      const threshold = 50;
      const isAtBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight <
        threshold;

      setAutoScrollEnabled(isAtBottom);
    }, 150);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (autoScrollEnabled && !isUserScrollingRef.current && container) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages, autoScrollEnabled]);

  useEffect(() => {
    setAutoScrollEnabled(true);
  }, [messages.length]);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Box
      ref={containerRef}
      onScroll={handleScroll}
      sx={{
        backgroundColor: 'background.paper',
        flex: 1,
        overflow: 'auto',
        px: 5,
        py: 3,
      }}
    >
      <Stack spacing={2}>
        {readyMessages.map((message, index) => {
          const offer = offeredLookup(message);
          return (
            <Fragment key={message.id}>
              <ChatMessage
                message={message}
                onFeedback={
                  message.role === 'assistant' && onFeedback
                    ? isPositive => onFeedback(index, isPositive)
                    : undefined
                }
              />
              {offer && (
                <LookupProposal
                  messageId={message.id}
                  offer={offer}
                  canAnswer={!isActive && index === messages.length - 1}
                />
              )}
            </Fragment>
          );
        })}
        {isActive && (
          <AssistantMessage message={streamingMessage} isStreaming />
        )}
      </Stack>
    </Box>
  );
};
